import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, Outlet, useNavigate as useRouterNavigate } from 'react-router-dom';
import Menu1 from './layout/Menu1';
import PageTitleUpdater from './layout/PageTitleUpdater'; // New import
// §1b: `AppShell` = chrome persistente del subárbol autenticado (layout route). El panel
// despachado por ruta es un contenedor por familia (`features/<f>/containers/*Screen.jsx`,
// `layout/SpaceHomeScreen.jsx`), elemento del `<Outlet/>` de `AppShell` vía `ConnectedSpace`.
import AppShell from './layout/AppShell';
import Login from './pages/Login';

// §1b — per-family route screens carved out of `SpaceScreen`. Lazy: one chunk per family.
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: estado derivado: se resuelve el flag 'espacio ya resuelto' en función de si hay sesión/space, no hay ciclo (una sola pasada por sesión).
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const TasksScreen = lazy(() => import('./features/tasks/containers/TasksScreen'));
const CalendarScreen = lazy(() => import('./features/calendar/containers/CalendarScreen'));
const DocsScreen = lazy(() => import('./features/docs/containers/DocsScreen'));
const UserProfileScreen = lazy(() => import('./features/profile/containers/UserProfileScreen'));
const DatabaseScreen = lazy(() => import('./features/databases/containers/DatabaseScreen'));
const ProfileScreen = lazy(() => import('./features/spaces/containers/ProfileScreen'));
const SpaceHomeScreen = lazy(() => import('./layout/SpaceHomeScreen'));
import { ChunkErrorBoundary } from './shared/errors/ChunkErrorBoundary';
import { useSessionStatus, useCurrentUser, useCurrentSpace, useAuthUser } from './context/authHooks';
import { SettingsProvider } from './context/SettingsContext';
import PayView from './pages/PayView';
import ApiDocs from './pages/ApiDocs';
import PublicClientForm from './pages/PublicClientForm';
import PublicBooking from './pages/PublicBooking';
import InteractiveFrameViewer from './pages/InteractiveFrameViewer';
import PublicBookingTemporal from './pages/PublicBookingTemporal';
import PublicSessionView from './pages/PublicSessionView';
import ResetPasswordPage from './pages/ResetPasswordPage';

const StandaloneTaskScreen = lazy(() => import('./features/tasks/containers/StandaloneTaskScreen'));
const DatabaseViewPage = lazy(() => import('./features/databases/containers/DatabaseViewPage'));
const PublicTaskView = lazy(() => import('./features/tasks/containers/PublicTaskView'));
// Landing pública. Lazy a propósito: arrastra `gsap` + ~2k líneas de escenas que no deben
// entrar al bundle principal de la app autenticada. Ver `.claude/plans/main-to-dev-integration/routing-reconciliation.md`.
const LandingPage = lazy(() => import('./pages/Landing/LandingPage'));
const WhatsMobileScreen = lazy(() => import('./features/client/containers/WhatsMobileScreen'));
const InteractiveFramesAdmin = lazy(() => import('./features/interactive-frames/containers/InteractiveFramesAdmin'));
const TemporalCardPage = lazy(() => import('./pages/TemporalCardPage'));

// Gate for the connected `/:spaceSlug` subtree. Works both as a wrapper (`<RequireConnected>…`)
// and as a layout route (`element={<RequireConnected />}` → renders `<Outlet />`).
// frontend-restructuring §1a: applied once as a layout route instead of ~95 times inline.
const RequireConnected = ({ children }) => {
  const { loading, isDbConnected } = useSessionStatus();

  if (loading) {
    return <div className="p-10 text-white">Loading...</div>;
  }

  if (!isDbConnected) {
    return <Navigate to="/" replace />;
  }

  return children ?? <Outlet />;
};

// The gate wrapping every connected `/:spaceSlug` route's per-family screen (via `space()`).
// `AppShell` (the layout route) already handled `loading`; this only decides what a
// *not-connected* user sees:
//   - `loginInPlace` routes (index, `event/:eventId`, a real task UUID on `:uuid`) → `<Login/>`
//     in place, URL kept — for shared task/event links opened logged-out.
//   - everything else → bounce to `/` (which redirects to the login screen at `/all`).
// (`Map` / the chrome are in `AppShell` and never remount; a family screen remounts only when
// you cross to another family.)
const TASK_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ConnectedSpace = ({ loginInPlace, uuidRoute, children }) => {
  const { loading, isDbConnected } = useSessionStatus();
  const { uuid } = useParams();
  if (loading) return <div className="p-10 text-white">Loading...</div>;
  if (!isDbConnected) {
    const keepUrl = loginInPlace && (!uuidRoute || TASK_UUID_RE.test(uuid || ''));
    return keepUrl ? <Login /> : <Navigate to="/" replace />;
  }
  return children;
};

// Route element helper: the connected gate + a lazy per-family screen. `gate` carries
// `loginInPlace` / `uuidRoute` for the standalone-link / index routes.
const space = (Screen, gate) => (
  <ConnectedSpace {...gate}><Suspense fallback={null}><Screen /></Suspense></ConnectedSpace>
);

const SpaceLayout = () => {
    const { spaceSlug } = useParams();
    // 03.1-auth-store.md §6 grupo 4 -- solo usa dbId/currentSpace (+ el setter), dentro del
    // alcance angosto combinado de `useCurrentUser()`+`useCurrentSpace()`. `setCurrentSpace`
    // hace exactamente `updateUser({ currentSpace })`, mismo comportamiento que `updateUser`
    // directo tenía acá.
    const { dbId } = useCurrentUser();
    const { currentSpace, setCurrentSpace } = useCurrentSpace();
    const navigate = useRouterNavigate();
    const [isSpaceResolved, setIsSpaceResolved] = useState(false);
    const prevDbIdRef = useRef(dbId);

    useEffect(() => {
        // Bug de carrera (space-default-onboarding, 2026-07-15): el login siempre sucede con el
        // usuario parado en /all (ahí vive el formulario de `Login`, montado por `RootHandler`
        // dentro de este mismo `SpaceLayout`). Cuando `finalizeLogin` resuelve un `currentSpace`
        // real (el space default u otro ya elegido antes) el `setUser` puede commitear ANTES de
        // que `navigate('/')` (en `Login.jsx`) alcance a sacar a este componente de /all --
        // porque `login()` es async y el `await` de por medio rompe el batching de React con la
        // llamada a `navigate` del caller. En ese commit intermedio, este efecto ve
        // spaceSlug==='all' + un currentSpace recién resuelto y lo tira a null antes de que la
        // redirección real ocurra, dejando al usuario varado en /all para siempre. Se detecta
        // ese tick exacto (dbId pasa de nada a algo) y se salta el reset sólo ahí -- cualquier
        // visita deliberada a /all después de eso (dbId sin cambios) sigue reseteando igual.
        const justAuthenticated = !prevDbIdRef.current && !!dbId;
        prevDbIdRef.current = dbId;

        if (!dbId || !spaceSlug) {
            // Nada que resolver (usuario deslogueado, o su sesión aún no cargó) -- se deja
            // pasar para que RootHandler/RequireConnected decidan (Login o loading), en vez de
            // dejar isSpaceResolved en false para siempre y bloquear el render con `return null`.
            setIsSpaceResolved(true);
            return;
        }

        // Si el "slug" es en realidad un UUID de tarea (rutas standalone de tareas que caen aquí
        // vía la ruta ':uuid') o 'event' (overlay de evento), permitimos montar el layout inmediatamente
        // sin perder el currentSpace previo. La tarea/evento se encarga de mostrarse por encima.
        if (spaceSlug === 'event' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(spaceSlug)) {
            setIsSpaceResolved(true);
            return;
        }

        if (spaceSlug.toLowerCase() === 'all') {
            if (!justAuthenticated && currentSpace !== null) {
                setCurrentSpace(null);
            }
            setIsSpaceResolved(true);
        } else {
            const decodedSlug = decodeURIComponent(spaceSlug).toLowerCase();

            if (spaceSlug !== decodedSlug) {
                const currentPath = window.location.pathname;
                const newPath = currentPath.replace(`/${spaceSlug}`, `/${decodedSlug}`);
                navigate(newPath + window.location.search, { replace: true });
                return;
            }

            if (currentSpace && currentSpace.name.toLowerCase() === decodedSlug) {
                setIsSpaceResolved(true);
                return;
            }
            fetch(`/api/user/${dbId}/spaces`)
              .then(res => res.json())
              .then(data => {
                  const matchedSpace = data.success && data.spaces
                      ? data.spaces.find(s => s.name.toLowerCase() === decodedSlug)
                      : null;
                  if (matchedSpace) {
                      setCurrentSpace(matchedSpace);
                      setIsSpaceResolved(true);
                  } else {
                      // Slug que no corresponde a ningún espacio real del usuario (typo, link
                      // viejo, o espacio del que ya no es miembro) -- antes se resolvía igual
                      // pero dejando la URL inválida en la barra de direcciones mientras el
                      // contenido ya mostraba "ALL". Se corrige la URL para que no quede
                      // desincronizada de lo que realmente se está mostrando.
                      navigate('/all', { replace: true });
                  }
              })
              .catch(err => {
                  console.error(err);
                  setIsSpaceResolved(true);
              });
        }
    }, [spaceSlug, dbId, currentSpace]); // Added currentSpace for proper resolution check

    if (!isSpaceResolved) {
        return null; // Block rendering until the URL is matched with the system state
    }

    return <Outlet />;
};

const RootRedirect = () => {
    // Space default onboarding (.claude/plans/space-default-onboarding.md, sección "3. Redirect
    // post-login"): reemplaza el `<Navigate to="/all" replace />` estático -- ese no podía saber
    // si el usuario ya tiene un `currentSpace` real (space default o el último que usó). Espera
    // a `spaceCheckDone` (mismo patrón que `isSpaceResolved` de `SpaceLayout`, arriba) para no
    // decidir "cae a /all" en el primer render, antes de que el fetch de `current_space` llegue.
    const { loading, spaceCheckDone, isDbConnected } = useSessionStatus();
    const { currentSpace } = useCurrentSpace();

    const location = useLocation();
    
    if (loading) return <div className="p-10 text-white">Loading...</div>;
    if (!isDbConnected) return <Navigate to={`/all${location.search}`} replace />;
    if (!spaceCheckDone) return null;

    const target = currentSpace?.name
        ? `/${encodeURIComponent(currentSpace.name)}`
        : '/all';

    return <Navigate to={target} replace />;
};

const TaskRouteResolver = () => {
    const { loading, isDbConnected } = useSessionStatus();
    const taskHoyUser = useAuthUser();
    if (loading) return null;

    // If connected, show the editable standalone view, otherwise show the read-only public view.
    if (isDbConnected) {
        return (
            <Suspense fallback={null}>
                <StandaloneTaskScreen user={taskHoyUser} />
            </Suspense>
        );
    }
    
    return (
        <Suspense fallback={null}>
            <PublicTaskView />
        </Suspense>
    );
};

// Entradas públicas / standalone: renderizan su propia UI a pantalla completa, sin el chrome
// de la app. Cada epic de la integración main→dev agrega su prefijo (A2: landing; A3: /pt/;
// B1: /s/ y /p/). Ver `routing-reconciliation.md`.
const PUBLIC_STANDALONE_PREFIXES = ['/pay/', '/t/', '/landing', '/reset-password', '/s/', '/p/', '/pt/', '/w/', '/w', '/if/', '/temporal/'];

const AppLayout = () => {
  const { isDbConnected } = useSessionStatus();
  const location = useLocation();
  const isPublicStandalone = PUBLIC_STANDALONE_PREFIXES.some((p) =>
    location.pathname.toLowerCase().startsWith(p),
  );
  const showMenu = isDbConnected && !isPublicStandalone;

  return (
    <div className="app-container" style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      {showMenu && <Menu1 />}
      <main style={{ width: '100vw', height: '100vh', position: 'absolute', top: 0, left: 0 }}>
        <ChunkErrorBoundary>
            <Routes>
              {/* Landing pública (scrollytelling). `LandingPage` maneja su propio scroll
                  vía la clase `landing-active` en <body>/<html>. */}
              <Route path="/landing" element={<Suspense fallback={null}><LandingPage /></Suspense>} />
              <Route path="/LANDING" element={<Navigate to="/landing" replace />} />

              {/* Public checkout route */}
              <Route path="/pay/:id" element={<PayView />} />

              {/* Public Client Form Route */}
              <Route path="/s/:uuid" element={<PublicClientForm />} />
              {/* Sesión pública de videollamada de terminal (integración main→dev B1). 3
                  segmentos → nunca colisiona con `/s/:uuid` (1 segmento). */}
              <Route path="/s/:space/:terminalId/:sessionId" element={<PublicSessionView />} />

              {/* Public Calendly/Booking Route */}
              <Route path="/p/:uuid" element={<PublicBooking />} />
              <Route path="/if/:id/*" element={<InteractiveFrameViewer />} />
              
              {/* Temporal Cards Route */}
              <Route path="/temporal/card" element={<Suspense fallback={null}><TemporalCardPage /></Suspense>} />
              
              {/* Reserva pública por enlace temporal de 24h (integración main→dev B1). */}
              <Route path="/pt/:uuid" element={<PublicBookingTemporal />} />

              {/* Public account-recovery route -- reached from the link ForgotPassword.js emails */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Redirect old login to root */}
              <Route path="/login" element={<Navigate to="/" replace />} />
              
              {/* Redirige al space real del usuario (default "General" u otro ya elegido antes),
                  o a /all si aún no hay ninguno resuelto */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Standalone Task Route */}
              <Route path="/t/:taskId" element={<TaskRouteResolver />} />
              
              {/* Standalone WhatsApp Mobile PWA Route */}
              <Route path="/w/*" element={<Suspense fallback={null}><WhatsMobileScreen /></Suspense>} />
              
              {/* Legacy Routes Redirects */}
              <Route path="/profile/*" element={<Navigate to="/all/profile" replace />} />
              <Route path="/database/*" element={<Navigate to="/all/database" replace />} />
              <Route path="/docs/*" element={<Navigate to="/all/docs" replace />} />
              <Route path="/calendar/*" element={<Navigate to="/all/calendar" replace />} />
              <Route path="/task/*" element={<Navigate to="/all/task" replace />} />
              <Route path="/dapps/*" element={<Navigate to="/all/dapps" replace />} />
              <Route path="/pdfeditor/*" element={<Navigate to="/all/pdfeditor" replace />} />
              <Route path="/walletsmanager/*" element={<Navigate to="/all/walletsmanager" replace />} />
              <Route path="/iaatomat/*" element={<Navigate to="/all/iaatomat" replace />} />
              <Route path="/docusafe/*" element={<Navigate to="/all/docusafe" replace />} />
              <Route path="/connetgoogle/*" element={<Navigate to="/all/connetgoogle" replace />} />
              <Route path="/notion/*" element={<Navigate to="/all/notion" replace />} />
              <Route path="/imapmail/*" element={<Navigate to="/all/imapmail" replace />} />
              <Route path="/exchange/*" element={<Navigate to="/all/exchange" replace />} />
              <Route path="/spei/*" element={<Navigate to="/all/spei" replace />} />
              <Route path="/facturador/*" element={<Navigate to="/all/facturador" replace />} />
              <Route path="/boo/*" element={<Navigate to="/all/boo" replace />} />
              <Route path="/whats/*" element={<Navigate to="/all/whats" replace />} />
              <Route path="/cosmicphone/*" element={<Navigate to="/all/cosmicphone" replace />} />
              <Route path="/virtualphone/*" element={<Navigate to="/all/virtualphone" replace />} />
              <Route path="/jorgebot/*" element={<Navigate to="/all/jorgebot" replace />} />
              <Route path="/clientes/*" element={<Navigate to="/all/clientes" replace />} />
              <Route path="/views/*" element={<Navigate to="/all/views" replace />} />
              <Route path="/recordings/*" element={<Navigate to="/all/recordings" replace />} />
              <Route path="/semantic/*" element={<Navigate to="/all/semantic" replace />} />
              <Route path="/payment/*" element={<Navigate to="/all/payment" replace />} />
              <Route path="/apis" element={<ApiDocs />} />
              
              {/* Space Router */}
              <Route path="/:spaceSlug" element={<SpaceLayout />}>
                  {/* Full-page DB view — its own lazy chunk, NOT under the AppShell chrome. */}
                  <Route path="database/view/:iddata" element={
                      <RequireConnected><Suspense fallback={null}><DatabaseViewPage /></Suspense></RequireConnected>
                  } />

                  {/* Everything else renders the persistent `AppShell` chrome (layout route).
                      Each family has its own lazy screen (§1b). `Map` / the chrome never
                      remount; `Menu2` remounts only on cross-family navigation. */}
                  <Route element={<AppShell />}>
                      {/* Index / standalone deep-links / dapp panels / unknown → SpaceHomeScreen. */}
                      <Route index element={space(SpaceHomeScreen, { loginInPlace: true })} />
                      <Route path="event/:eventId" element={space(SpaceHomeScreen, { loginInPlace: true })} />
                      <Route path=":uuid" element={space(SpaceHomeScreen, { loginInPlace: true, uuidRoute: true })} />

                      <Route path="task/*" element={space(TasksScreen)} />
                      <Route path="if/*" element={space(InteractiveFramesAdmin)} />
                      <Route path="calendar/*" element={space(CalendarScreen)} />
                      <Route path="docs" element={space(DocsScreen)} />
                      <Route path="docs/:docId" element={space(DocsScreen)} />
                      <Route path="user/:userId" element={space(UserProfileScreen)} />
                      <Route path="database/user/:userId" element={space(UserProfileScreen)} />
                      <Route path="database/user/:userId/:tabId" element={space(UserProfileScreen)} />

                      {/* database family (§1b carve). Single-segment routes kept explicit so
                          `:uuid` doesn't swallow them; the `:clienteShortId` routes so
                          `useParams()` resolves for the client short-URL overlay. */}
                      <Route path="database" element={space(DatabaseScreen)} />
                      <Route path="database/*" element={space(DatabaseScreen)} />
                      <Route path="clientes" element={space(DatabaseScreen)} />
                      <Route path="views" element={space(DatabaseScreen)} />
                      <Route path="recordings" element={space(DatabaseScreen)} />
                      <Route path="semantic" element={space(DatabaseScreen)} />
                      <Route path="payment" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/fact" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/otc" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/paylink" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/wallet" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/soft" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/tpv" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/fact/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/fact/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/otc/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/otc/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/paylink/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/paylink/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/wallet/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/wallet/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/soft/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/soft/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/tpv/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/tpv/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/tpv/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/tpv/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/cosmic/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/cosmic/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/:clienteShortId" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/:clienteShortId/:tab" element={space(DatabaseScreen)} />
                      <Route path="database/clientes/:clienteShortId/:tab/:nodeId" element={space(DatabaseScreen)} />

                      {/* profile / spaces family (§1b carve) — the space dashboard. */}
                      <Route path="profile" element={space(ProfileScreen)} />
                      <Route path="profile/:spaceName" element={space(ProfileScreen)} />
                      <Route path="profile/:spaceName/:spaceTab" element={space(ProfileScreen)} />
                      <Route path="profile/:spaceName/:spaceTab/:dappId" element={space(ProfileScreen)} />
                      <Route path="maptools/*" element={space(SpaceHomeScreen)} />

                      {/* Unknown path under a space → the map home. */}
                      <Route path="*" element={space(SpaceHomeScreen)} />
                  </Route>
              </Route>
            </Routes>
        </ChunkErrorBoundary>
      </main>
    </div>
  )
}

const App = () => {
  return (
    <SettingsProvider>
      <Router>
        <PageTitleUpdater />
        <AppLayout />
      </Router>
    </SettingsProvider>
  );
}

export default App;
